import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from 'react-router-dom';

import { isElectron, isCapacitor, isNativeApp } from './config';
const Router = (isElectron || isCapacitor) ? HashRouter : BrowserRouter;
import './App.css';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import AuthModal from './components/AuthModal';
import QuantumBackground from './components/QuantumBackground';
import PricingModal from './components/PricingModal';
import NotificationToast from './components/NotificationToast';
import GlobalSearch from './components/GlobalSearch';
import UpdateNotification from './components/UpdateNotification';
import ErrorBoundary, { withErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import useGlobalSearch from './hooks/useGlobalSearch';
import useDashboardStore from './store/dashboardStore';
import { ssoService } from './services/sso';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// PERFORMANCE: Lazy load pages for code splitting
// Critical path pages loaded eagerly
import Dashboard from './pages/Dashboard';
import Scan from './pages/Scan';

// PERFORMANCE: Heavy pages lazy loaded to reduce initial bundle size
const Marketplace = lazy(() => import('./pages/Marketplace'));
const AIAgents = lazy(() => import('./pages/AIAgents'));
const Analytics = lazy(() => import('./pages/Analytics')); // Contains recharts (~65KB)
const Scrapers = lazy(() => import('./pages/Scrapers'));
const Settings = lazy(() => import('./pages/Settings'));
const Monitoring = lazy(() => import('./pages/Monitoring'));
const SellerAccounts = lazy(() => import('./pages/SellerAccounts'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const Legal = lazy(() => import('./pages/Legal'));
const OnboardingWizard = lazy(() => import('./components/OnboardingWizard'));
const VideoGenerator = lazy(() => import('./pages/VideoGenerator'));
const ActivityFeedPage = lazy(() => import('./pages/ActivityFeedPage'));

// New ecosystem management pages
const UserManagement = lazy(() => import('./pages/UserManagement'));
const SystemHealth = lazy(() => import('./pages/SystemHealth'));
const DataExport = lazy(() => import('./pages/DataExport'));
const EcosystemOnboarding = lazy(() => import('./pages/EcosystemOnboarding'));

// Loading fallback component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '50vh',
    color: '#00d4ff',
    fontFamily: 'Orbitron, monospace',
  }}>
    <div className="loading-spinner" style={{ marginRight: '15px' }}></div>
    Loading...
  </div>
);

// New SaaS pages
import LandingPage from './pages/LandingPage';
import AppMarketplace from './pages/AppMarketplace';
import Billing from './pages/Billing';
import Support from './pages/Support';

const DashboardPage = withErrorBoundary(Dashboard, {
  title: 'Dashboard temporarily unavailable',
  description: 'We ran into a problem loading live metrics. Please try again.',
});

const MarketplacePage = withErrorBoundary(Marketplace, {
  title: 'Marketplace search unavailable',
  description: 'Try refreshing the page or check back in a moment.',
});

const AIAgentsPage = withErrorBoundary(AIAgents, {
  title: 'AI Agents panel failed to load',
  description: 'Reconnect or refresh to resume monitoring.',
});

const AnalyticsPage = withErrorBoundary(Analytics, {
  title: 'Analytics not available right now',
  description: 'We were unable to load analytics data. Please retry soon.',
});

const ScanPage = withErrorBoundary(Scan, {
  title: 'Product scan issue',
  description: 'Barcode scanning is momentarily unavailable.',
});

const ScrapersPage = withErrorBoundary(Scrapers, {
  title: 'Scraper dashboard failed to render',
  description: 'Refresh to attempt reconnection with live scraper data.',
});

const SettingsPage = withErrorBoundary(Settings, {
  title: 'Settings unavailable',
  description: 'Please refresh or contact support if the issue persists.',
});

const MonitoringPage = withErrorBoundary(Monitoring, {
  title: 'Monitoring dashboard unavailable',
  description: 'System metrics cannot be loaded right now.',
});

const SellerAccountsPage = withErrorBoundary(SellerAccounts, {
  title: 'Seller accounts unavailable',
  description: 'We were unable to load seller account data. Please try again soon.',
});

const HelpPageWrapped = withErrorBoundary(HelpPage, {
  title: 'Help page unavailable',
  description: 'We could not load the help documentation. Please try again.',
});

const VideoGeneratorPageWrapped = withErrorBoundary(VideoGenerator, {
  title: 'Video generator unavailable',
  description: 'We could not load the video generation interface. Please try again.',
});

const AppMarketplacePage = withErrorBoundary(AppMarketplace, {
  title: 'App Marketplace unavailable',
  description: 'We could not load the app marketplace. Please try again.',
});

const BillingPage = withErrorBoundary(Billing, {
  title: 'Billing page unavailable',
  description: 'We could not load billing information. Please try again.',
});

const SupportPage = withErrorBoundary(Support, {
  title: 'Support center unavailable',
  description: 'We could not load the support center. Please try again.',
});

const ActivityFeedPageWrapped = withErrorBoundary(ActivityFeedPage, {
  title: 'Activity feed unavailable',
  description: 'We could not load the ecosystem activity feed. Please try again.',
});

// New ecosystem management pages with error boundaries
const UserManagementPage = withErrorBoundary(UserManagement, {
  title: 'User management unavailable',
  description: 'We could not load the user management panel. Please try again.',
});

const SystemHealthPage = withErrorBoundary(SystemHealth, {
  title: 'System health unavailable',
  description: 'We could not load system health data. Please try again.',
});

const DataExportPage = withErrorBoundary(DataExport, {
  title: 'Data export unavailable',
  description: 'We could not load the data export interface. Please try again.',
});

const EcosystemOnboardingPage = withErrorBoundary(EcosystemOnboarding, {
  title: 'Onboarding unavailable',
  description: 'We could not load the onboarding flow. Please try again.',
});

function AppInner() {
  const {
    isAuthenticated, isDemoMode, showAuthModal, authModalView,
    login, logout, enterDemoMode, openAuthModal, closeAuthModal,
  } = useAuth();

  const initialize = useDashboardStore((state) => state.initialize);
  const cleanup = useDashboardStore((state) => state.cleanup);
  const loadDemoData = useDashboardStore((state) => state.loadDemoData);
  const loading = useDashboardStore((state) => state.loading);
  const error = useDashboardStore((state) => state.error);
  const [showPricing, setShowPricing] = React.useState(false);
  const [notification, setNotification] = React.useState(null);

  // Global search hook -- manages all search state, keyboard shortcuts, and result filtering
  const search = useGlobalSearch();

  // Handle SSO callback on app load
  useEffect(() => {
    const checkSSO = async () => {
      const ssoSuccess = await ssoService.handleSSOCallback();
      if (ssoSuccess) {
        login({ user: null });
        toast.success('Signed in via SSO');
        initialize();
      }
    };
    checkSSO();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isNativeApp || isDemoMode) {
      cleanup();
      loadDemoData();
      return () => {
        cleanup();
      };
    }

    initialize();
    return () => {
      cleanup();
    };
  }, [initialize, cleanup, loadDemoData, isDemoMode]);

  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleLoginSuccess = (loginData) => {
    login(loginData);
    showNotification('Welcome back!', 'success');
    initialize();
  };

  const handleDemoMode = () => {
    enterDemoMode();
    showNotification('Exploring the interactive demo experience', 'info');
  };

  const handleLogout = () => {
    logout();
    cleanup();
    showNotification('Signed out successfully', 'info');
  };

  // Show landing page with auth modal for non-authenticated users
  if (!isAuthenticated) {
    return (
      <>
        <QuantumBackground />
        <LandingPage
          onLogin={() => openAuthModal('login')}
          onSignup={() => openAuthModal('register')}
          onDemo={handleDemoMode}
        />
        <AuthModal
          isOpen={showAuthModal}
          onClose={closeAuthModal}
          onSuccess={handleLoginSuccess}
          onDemo={handleDemoMode}
          initialView={authModalView}
        />
        <ToastContainer
          position="top-right"
          autoClose={3500}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss={false}
          theme="dark"
        />
      </>
    );
  }

  return (
    <>
      {/* Skip to main content link - WCAG 2.1 AA */}
      <a href="#saas-main" className="sr-only sr-only-focusable">Skip to main content</a>

      <QuantumBackground />
      <div className="app-container">
        <Sidebar />

        <main id="saas-main" className="main-content" role="main">
          <Header
            // Apple builds ship free (no IAP / 3.1.1): no pricing/upgrade CTA.
            onShowPricing={isNativeApp ? undefined : () => setShowPricing(true)}
            onLogout={handleLogout}
            onOpenSearch={search.open}
            isDemo={isDemoMode}
          />

          {isDemoMode && (
            <div className="demo-banner">
              <strong>Demo Mode:</strong> You're viewing simulated QuantrolixAI data. Sign in to connect your live marketplaces.
            </div>
          )}

          {error && (
            <div className="error-message" role="alert">
              Error loading dashboard: {error}
            </div>
          )}

          <ErrorBoundary title="Navigation error" description="We could not render this section.">
            {/* PERFORMANCE: Suspense wrapper for lazy-loaded routes */}
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/marketplace" element={<MarketplacePage />} />
                <Route path="/ai-agents" element={<AIAgentsPage />} />
                <Route path="/analytics" element={<AnalyticsPage />} />
                <Route path="/scan" element={<ScanPage />} />
                <Route path="/scrapers" element={<ScrapersPage />} />
                <Route path="/seller-accounts" element={<SellerAccountsPage />} />
                <Route path="/monitoring" element={<MonitoringPage />} />
                <Route path="/settings" element={<SettingsPage />} />
                <Route path="/help" element={<HelpPageWrapped />} />
                <Route path="/legal" element={<Legal />} />
                <Route path="/video-generator" element={<VideoGeneratorPageWrapped />} />
                <Route path="/apps" element={<AppMarketplacePage />} />
                {/* Billing/subscription management is web-only — Apple builds ship free (3.1.1) */}
                {!isNativeApp && <Route path="/billing" element={<BillingPage />} />}
                <Route path="/support" element={<SupportPage />} />
                <Route path="/activity" element={<ActivityFeedPageWrapped />} />
                {/* Ecosystem management routes */}
                <Route path="/users" element={<UserManagementPage />} />
                <Route path="/system-health" element={<SystemHealthPage />} />
                <Route path="/data-export" element={<DataExportPage />} />
                <Route path="/onboarding" element={<EcosystemOnboardingPage />} />
                <Route path="*" element={
                  <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
                    <h1 className="text-6xl font-bold text-purple-400 mb-4">404</h1>
                    <p className="text-xl text-gray-300 mb-6">Page not found</p>
                    <p className="text-gray-500 mb-8 max-w-md">The page you're looking for doesn't exist or has been moved.</p>
                    <a href="/dashboard" className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors">
                      Back to Dashboard
                    </a>
                  </div>
                } />
              </Routes>
            </Suspense>
          </ErrorBoundary>

          {/* Onboarding wizard for new users - lazy loaded */}
          <Suspense fallback={null}>
              <OnboardingWizard
              onComplete={() => showNotification('Onboarding complete!', 'success')}
              onSkip={() => showNotification('You can restart the tour from Settings anytime', 'info')}
            />
          </Suspense>

          {loading && (
            <div className="loading-overlay">
              <div className="loading-spinner"></div>
            </div>
          )}
        </main>
      </div>

      {/* Global Search Command Palette */}
      <GlobalSearch
        isOpen={search.isOpen}
        close={search.close}
        query={search.query}
        setQuery={search.setQuery}
        debouncedQuery={search.debouncedQuery}
        results={search.results}
        flatItems={search.flatItems}
        selectedIndex={search.selectedIndex}
        setSelectedIndex={search.setSelectedIndex}
        handleKeyDown={search.handleKeyDown}
        executeItem={search.executeItem}
        tasksLoading={search.tasksLoading}
        recentSearches={search.recentSearches}
        handleClearRecentSearches={search.handleClearRecentSearches}
        handleSelectRecent={search.handleSelectRecent}
      />

      {showPricing && !isNativeApp && (
        <PricingModal
          onClose={() => setShowPricing(false)}
          onNotification={showNotification}
        />
      )}

      <UpdateNotification />

      {notification && (
        <NotificationToast
          message={notification.message}
          type={notification.type}
        />
      )}
      <ToastContainer
        position="top-right"
        autoClose={3500}
        newestOnTop
        closeOnClick
        pauseOnFocusLoss={false}
        theme="dark"
      />
    </>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <AuthProvider>
          <AppInner />
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
